import { Button } from '@helix-ds/react'
import { Navbar } from '@/components/Navbar'
import { OrderFormViewModal } from '@/components/modals/OrderFormViewModal'
import { OrderExternalIdLabel } from '@/components/order/OrderExternalIdLabel'
import { OrderReportDocumentsMenu } from '@/components/order/OrderReportDocumentsMenu'
import { CriticalResultBanner } from '@/components/order/CriticalResultBanner'
import { InconclusiveGuidanceBanner } from '@/components/order/InconclusiveGuidanceBanner'
import { OrderDetailsPanel } from '@/components/order/OrderDetailsPanel'
import TestResultsCard from '@/components/order/TestResultsCard'
import { getClientName, isCaseConnectEnabled } from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import { getAllOrderReports } from '@/features/report/services/reportClient'
import { useCriticalResultAcknowledgement } from '@/features/report/useCriticalResultAcknowledgement'
import {
  selectFinalGuidanceReport,
  selectLatestReport,
  inconclusiveClassificationExplanation,
} from '@/features/report/selectFinalGuidanceReport'
import { useConfiguredOrderSteps } from '@/hooks/useConfiguredOrderSteps'
import { legacyTrfPayloadToCanonical } from '@/ordering/canonicalOrder'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setCanonicalOrder } from '@/store/slices/canonicalOrderSlice'
import type { OrderFormInfoData } from '@/types/RequisitionFormTypes'
import type { OrderReportResponse } from '@/types/OrderReportTypes'
import { getOrderById, inflectionExternalIdFromOrder } from '@/utils/DatabaseOperations'
import { formatAge, formatBirthDate, formatMrnForDisplay, formatPatientNameLastFirst } from '@/utils/orderFormUtils'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { Lightbulb } from 'flowbite-react-icons/outline'
import { OrderStepsRow } from '@/components/order/OrderStepsRow'
import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { PermissionGuard } from '@/features/authorization/PermissionGuard'
import { CaseConnectError, createCaseForOrder, getCaseByOrder } from '@/features/caseConnect/services/caseConnectClient'

type OrderGuidanceLocationState =
  | {
      epicState?: string
      orderId?: string
      orderData?: OrderFormInfoData
    }
  | string
  | null
  | undefined

export function OrderGuidance() {
  const { config } = useUiConfig()
  const location = useLocation()
  const params = useParams<{ orderId?: string }>()
  const { user: auth0User, logout: auth0Logout } = useAuth0()
  const dispatch = useAppDispatch()

  const locationState = location.state as OrderGuidanceLocationState
  const orderDataFromState =
    typeof locationState === 'object' && locationState !== null ? locationState.orderData : undefined
  const epicState =
    typeof locationState === 'object' && locationState !== null
      ? (locationState.epicState ?? (locationState as { state?: string }).state)
      : null
  const authFlow = epicState ? 'epic' : 'standalone'

  const orderId = params.orderId
  const [fetchedOrderData, setFetchedOrderData] = useState<OrderFormInfoData | undefined>(undefined)
  const orderData = orderDataFromState ?? fetchedOrderData
  const [inflectionExternalId, setInflectionExternalId] = useState<string | null>(null)

  const [reports, setReports] = useState<OrderReportResponse[]>([])
  const [reportsLoading, setReportsLoading] = useState(false)
  const [reportsError, setReportsError] = useState<string | null>(null)
  const [isOrderFormModalOpen, setIsOrderFormModalOpen] = useState(false)
  const [mainPanelView, setMainPanelView] = useState<'guidance' | 'orderDetails'>('guidance')

  const { canonicalOrder: storedCanonical } = useAppSelector((s) => s.canonicalOrder)

  const handleLogout = () => {
    auth0Logout({ logoutParams: { returnTo: window.location.origin + '/login' } })
  }

  const reportCaller = useMemo(
    () => auth0User?.email || auth0User?.sub || 'unknown-user',
    [auth0User?.email, auth0User?.sub]
  )

  const navigate = useNavigate()
  const [startingCase, setStartingCase] = useState(false)
  const [startCaseError, setStartCaseError] = useState<string | null>(null)
  // Whether a Case Connect case already exists for this order. null = unknown
  // (not yet resolved); false = confirmed none (show "New Case Connect");
  // true = one exists (offer the view link, hide the create button).
  const [caseExists, setCaseExists] = useState<boolean | null>(null)

  /**
   * Start (or open) a Case Connect conference for this order, then navigate to
   * the case page. Idempotent server-side, so if a case already exists create
   * returns it and we simply navigate. On failure, show an inline error and do
   * not navigate (the guidance page keeps working).
   */
  const handleStartCase = async () => {
    if (!orderId) return
    setStartingCase(true)
    setStartCaseError(null)
    try {
      await createCaseForOrder(orderId, reportCaller)
      setCaseExists(true)
      navigate(`/order/case-connect/${orderId}`)
    } catch (err) {
      Sentry.captureException(err, { tags: { operation: 'order.guidance.start-case' } })
      setStartCaseError(err instanceof Error ? err.message : 'Failed to start Case Connect.')
    } finally {
      setStartingCase(false)
    }
  }

  // Resolve whether a Case Connect case already exists for this order, so the
  // guidance header can show "New Case Connect" only when none exists (a 404
  // from the by-order lookup means no case yet). Any other error leaves the
  // state unknown (null) so we don't offer create against an ambiguous result.
  useEffect(() => {
    if (!orderId || !isCaseConnectEnabled(config)) return
    let cancelled = false
    const run = async () => {
      try {
        await getCaseByOrder(orderId, reportCaller)
        if (!cancelled) setCaseExists(true)
      } catch (err) {
        if (cancelled) return
        if (err instanceof CaseConnectError && err.status === 404) {
          setCaseExists(false)
        }
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [orderId, reportCaller, config])

  // Load the order for the patient header (name / MRN / DOB) when not supplied via navigation state.
  useEffect(() => {
    if (orderDataFromState || fetchedOrderData || !orderId) return
    let cancelled = false
    const run = async () => {
      try {
        const order = await getOrderById(orderId, reportCaller)
        if (cancelled || !order) return
        const resolvedExternalId = inflectionExternalIdFromOrder(order)
        if (resolvedExternalId) setInflectionExternalId(resolvedExternalId)
        if (!order.json_payload) return
        if (order.json_payload.orderFormInfo) setFetchedOrderData(order.json_payload.orderFormInfo)
        if (order.json_payload.requisitionFormData) {
          dispatch(
            setCanonicalOrder(
              legacyTrfPayloadToCanonical({
                orderFormInfo: order.json_payload.orderFormInfo,
                requisitionFormData: order.json_payload.requisitionFormData,
              })
            )
          )
        }
      } catch (err) {
        Sentry.captureException(err, { tags: { operation: 'order.guidance.fetch-order' } })
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [orderId, orderDataFromState, fetchedOrderData, reportCaller, dispatch])

  // Fetch reports (includes the guidance envelope) for this order.
  useEffect(() => {
    if (!orderId) {
      setReports([])
      setReportsError(null)
      setReportsLoading(false)
      return
    }
    let cancelled = false
    const run = async () => {
      setReportsLoading(true)
      setReportsError(null)
      try {
        const result = await getAllOrderReports(orderId, reportCaller)
        if (cancelled) return
        setReports(result)
      } catch (err) {
        if (cancelled) return
        Sentry.captureException(err, { tags: { operation: 'order.guidance.fetch-reports' } })
        setReportsError(err instanceof Error ? err.message : 'Failed to fetch guidance.')
        setReports([])
      } finally {
        if (!cancelled) setReportsLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [orderId, reportCaller])

  const finalGuidanceReport = useMemo(() => selectFinalGuidanceReport(reports), [reports])

  // Surface the inconclusive-classification explanation (if any) from the final
  // guidance report, falling back to the latest report so an inconclusive
  // result still shows a notice even without finalized guidance.
  const inconclusiveExplanation = useMemo(() => {
    const source = finalGuidanceReport ?? selectLatestReport(reports)
    return source ? inconclusiveClassificationExplanation(source) : null
  }, [finalGuidanceReport, reports])

  const { isCriticalAndUnreviewed, criticalReportVersions, isConfirmingReview, confirmCriticalReview } =
    useCriticalResultAcknowledgement({
      orderId,
      reports,
      reportsLoading,
      reportsError: Boolean(reportsError),
      reportCaller,
    })

  const birthDate =
    orderData?.patient?.birthDate ?? storedCanonical?.patient?.dateOfBirth ?? null
  const patientName =
    orderData?.patient?.name?.firstName || orderData?.patient?.name?.lastName
      ? formatPatientNameLastFirst(orderData.patient.name)
      : formatPatientNameLastFirst(storedCanonical?.patient)
  const mrnDisplay = formatMrnForDisplay(orderData?.patient?.mrn || storedCanonical?.patient?.mrn || '')
  const ageDisplay = formatAge(birthDate)
  const dobDisplay = formatBirthDate(birthDate)

  const steps = useConfiguredOrderSteps({ currentStepId: 'guidance' })

  return (
    <div className={authFlow === 'standalone' ? 'page-container-standalone' : 'page-container'}>
      {authFlow === 'standalone' && (
        <Navbar
          userAvatar={auth0User?.picture}
          userName={auth0User?.name}
          onLogout={handleLogout}
          showNavLinks={true}
          organizationName={getClientName(config)}
        />
      )}
      <div className={`flex w-full flex-col gap-4 ${authFlow === 'standalone' ? 'px-6' : ''}`}>
        <div
          className={`sticky top-0 z-20 border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-sm ${
            authFlow === 'standalone' ? '-mx-6 border-b' : 'rounded-base border'
          }`}
        >
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
              <div className="flex flex-col gap-1">
                <h1 className="text-2xl font-semibold tracking-tight text-text-heading dark:text-white">
                  {patientName || 'Patient'}
                </h1>
                <span className="text-xs font-medium text-text-body dark:text-gray-400">
                  {mrnDisplay || 'MRN not available'}
                </span>
                {inflectionExternalId ? <OrderExternalIdLabel externalId={inflectionExternalId} /> : null}
              </div>
              <div className="hidden h-10 w-px bg-border-base sm:block" />
              <div className="flex flex-col gap-1">
                <span className="text-sm font-bold text-text-heading dark:text-white">
                  {ageDisplay || 'Age unavailable'}
                </span>
                <span className="text-xs font-medium text-text-body dark:text-gray-400">
                  {dobDisplay || 'DOB unavailable'}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <OrderReportDocumentsMenu
                orderId={orderId}
                orderData={orderData}
                onOpenOrderForm={() => setIsOrderFormModalOpen(true)}
                showViewLabReportButton={true}
                useParentReportList={true}
                parentReports={reports}
                parentReportsLoading={reportsLoading}
              />
              <button
                type="button"
                onClick={() => setMainPanelView((v) => (v === 'guidance' ? 'orderDetails' : 'guidance'))}
                className="inline-flex items-center gap-2 rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-text-body dark:text-gray-400 shadow-xs transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
              >
                {mainPanelView === 'guidance' ? 'Order Details' : 'Show Guidance'}
              </button>
              {orderId && isCaseConnectEnabled(config) && (
                <PermissionGuard permission="case:view">
                  <Link
                    to={`/order/case-connect/${orderId}`}
                    className="inline-flex items-center gap-2 rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-text-body dark:text-gray-400 shadow-xs transition-colors hover:bg-brand-600 hover:text-white"
                  >
                    Case Connect
                  </Link>
                </PermissionGuard>
              )}
              {orderId && isCaseConnectEnabled(config) && caseExists === false && (
                <PermissionGuard permission="case:manage_access">
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    disabled={startingCase}
                    onClick={() => void handleStartCase()}
                    className="focus-visible:ring-brand-600"
                  >
                    {startingCase ? 'Creating…' : 'New Case Connect'}
                  </Button>
                </PermissionGuard>
              )}
            </div>
          </div>
          {startCaseError && (
            <p className="mt-3 text-sm font-medium text-text-fg-error dark:text-error-100" role="alert">
              {startCaseError}
            </p>
          )}
        </div>

        <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-6 pb-6 pt-12 shadow-sm">
          <OrderStepsRow variant="tracker" steps={steps} labelTone="guidance" />
        </div>

        {isCriticalAndUnreviewed && (
          <CriticalResultBanner
            reports={criticalReportVersions}
            onAcknowledge={confirmCriticalReview}
            isConfirming={isConfirmingReview}
          />
        )}

        {inconclusiveExplanation && <InconclusiveGuidanceBanner explanation={inconclusiveExplanation} />}

        {mainPanelView === 'orderDetails' ? (
          <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-sm">
            <OrderDetailsPanel />
          </div>
        ) : (
          <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <Lightbulb className="h-5 w-5 shrink-0 text-text-fg-brand dark:text-brand-400" />
              <h2 className="text-lg font-semibold tracking-tight text-text-heading dark:text-white">Clinical Guidance</h2>
            </div>

            {reportsLoading ? (
              <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-text-body dark:text-gray-400 shadow-sm">
                Loading guidance…
              </div>
            ) : reportsError ? (
              <div className="rounded-base border border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 p-6 text-sm text-text-fg-error-strong dark:text-error-100 shadow-sm">
                {reportsError}
              </div>
            ) : finalGuidanceReport ? (
              <TestResultsCard
                orderId={orderId}
                orderData={orderData}
                useParentReportList={true}
                parentReports={[finalGuidanceReport]}
                parentReportsLoading={false}
                parentReportsError={null}
              />
            ) : (
              <div className="rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-text-body dark:text-gray-400 shadow-sm">
                Guidance is not yet available for this order.
              </div>
            )}
          </div>
        )}
      </div>

      <OrderFormViewModal
        isOpen={isOrderFormModalOpen}
        onClose={() => setIsOrderFormModalOpen(false)}
        canonical={storedCanonical}
        formData={null}
        orderData={orderData ?? null}
        orderId={orderId}
        caller={reportCaller}
      />
    </div>
  )
}

export default OrderGuidance
